/**
 * Row model for the Translate page's History panel.
 *
 * The panel used to read only `recentTranslations` from this browser's
 * localStorage, so a signed-in person who had translated on their phone (or
 * in another browser) saw an empty or short list and reasonably concluded
 * history was not syncing. It now renders the SAME account ledger the Library
 * page shows, and falls back to the local recents for guests or when that
 * ledger cannot be read.
 *
 * Two shapes go in — gateway/Library entries and local recents — and one
 * shape comes out, so the panel never branches on where a row came from.
 *
 * Pure and dependency-free (no react, no fetch, no storage) so it is
 * unit-testable the same way as ./gatewayLibraryMapping.ts.
 */

/** How many rows the panel renders. Beyond this it is a scroll graveyard, and
 * the ledger fetch itself already asks for far more than anyone reads. */
export const HISTORY_PANEL_CAP = 100

export const HISTORY_LINE1_MAX = 48
export const HISTORY_LINE2_MAX = 56

/** Keep preview text compact without cutting a surrogate pair in half. */
export function truncatePreview(text: string, max: number): string {
  const normalized = text.replace(/\s+/g, ' ').trim()
  if (normalized === '') return ''
  if (max <= 1) return '…'
  const codePoints = Array.from(normalized)
  if (codePoints.length <= max) return normalized
  return `${codePoints.slice(0, max - 1).join('')}…`
}

export type HistoryGroupLabel = 'Today' | 'Yesterday' | 'Previous 7 Days' | 'Previous 30 Days' | 'Older'

const DAY_MS = 86_400_000

function startOfLocalDay(ts: number): number {
  const date = new Date(ts)
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
}

export function historyGroupLabel(ts: number, now: number): HistoryGroupLabel {
  if (ts <= 0) return 'Older'
  const diff = startOfLocalDay(now) - startOfLocalDay(ts)
  if (diff <= 0) return 'Today'
  if (diff <= DAY_MS) return 'Yesterday'
  if (diff <= 7 * DAY_MS) return 'Previous 7 Days'
  if (diff <= 30 * DAY_MS) return 'Previous 30 Days'
  return 'Older'
}

export function groupHistoryRows(
  rows: readonly HistoryRow[],
  now: number,
): Array<{ label: HistoryGroupLabel; rows: HistoryRow[] }> {
  const groups: Array<{ label: HistoryGroupLabel; rows: HistoryRow[] }> = []
  for (const row of rows) {
    const label = historyGroupLabel(row.ts, now)
    const group = groups.find((candidate) => candidate.label === label)
    if (group) group.rows.push(row)
    else groups.push({ label, rows: [row] })
  }
  return groups
}

export type HistoryRowKind = 'text' | 'image' | 'pdf'

/** What MediaResultModal needs to render an image/PDF result read-only. */
export interface HistoryRowMedia {
  id: string
  translated_text: string
  source_lang: string
  target_lang: string
  content_type: string
  image_url?: string
}

export interface HistoryRow {
  id: string
  kind: HistoryRowKind
  /** First line. For media rows the source text is usually empty, so this
   * falls back to a type label rather than rendering a blank row. */
  inputText: string
  /** Second line — the translation. */
  output: string
  sourceLang: string
  targetLang: string
  /** Epoch ms; 0 when the source carried no usable timestamp. */
  ts: number
  /** Present only on media rows. Their presence is what routes a click to the
   * read-only modal instead of the translator's restore path. */
  media?: HistoryRowMedia
}

/** Structural subset of `LibraryEntry` (./gatewayLibraryMapping) — declared
 * locally so this module stays importable by a test that does not pull the
 * whole library-source graph. */
export interface HistoryLedgerEntryLike {
  id: string
  source_text?: string
  translated_text?: string
  source_lang?: string
  target_lang?: string
  created_at?: string
  content_type?: string
  image_url?: string
}

/** Structural subset of `RecentTranslation` (src/components/translator/types). */
export interface HistoryRecentLike {
  id: string
  inputText?: string
  output?: string
  sourceLang?: string
  targetLang?: string
  tab?: string
  ts?: number
}

function text(v: unknown): string {
  return typeof v === 'string' ? v : ''
}

/**
 * A row is media when the ledger says so, or — for rows written before
 * `content_type` was populated — when it carries an image URL. 'document'
 * collapses onto 'pdf': the Documents tab is one tab, and the panel has one
 * file icon for it.
 */
export function historyRowKind(contentType: string | undefined, hasImageUrl = false): HistoryRowKind {
  if (contentType === 'image') return 'image'
  if (contentType === 'pdf' || contentType === 'document') return 'pdf'
  if (!contentType && hasImageUrl) return 'image'
  return 'text'
}

/** Media rows frequently have no source text — a type label beats a blank line. */
export function mediaRowLabel(kind: HistoryRowKind): string {
  return kind === 'image' ? 'Image Translation' : 'Document Translation'
}

function toTimestamp(iso: string | undefined): number {
  if (!iso) return 0
  const parsed = Date.parse(iso)
  return Number.isNaN(parsed) ? 0 : parsed
}

/**
 * Turns a stored `translated_text` into the one line the row shows.
 *
 * Passed in rather than imported: this module is loaded by its test as a
 * stripped standalone file, so it must stay import-free. `HistoryPanel`
 * supplies `previewFromTranslatedText` from ./mediaResultContent, which is
 * what keeps `{"kind":"summary","title":"N…` out of the sidebar.
 */
export type PreviewOf = (translatedText: string) => string

const identityPreview: PreviewOf = (t) => t

export function ledgerEntryToRow(
  entry: HistoryLedgerEntryLike,
  previewOf: PreviewOf = identityPreview,
): HistoryRow {
  const kind = historyRowKind(entry.content_type, !!entry.image_url)
  const sourceText = text(entry.source_text)
  const translated = text(entry.translated_text)
  const sourceLang = text(entry.source_lang) || 'en'
  const targetLang = text(entry.target_lang) || 'he'
  return {
    id: text(entry.id),
    kind,
    inputText: sourceText || (kind === 'text' ? '' : mediaRowLabel(kind)),
    // The preview is for reading and searching. `media.translated_text` below
    // keeps the RAW value, because the result dialog has to parse it.
    output: previewOf(translated),
    sourceLang,
    targetLang,
    ts: toTimestamp(entry.created_at),
    media:
      kind === 'text'
        ? undefined
        : {
            id: text(entry.id),
            translated_text: translated,
            source_lang: sourceLang,
            target_lang: targetLang,
            content_type: text(entry.content_type) || kind,
            image_url: text(entry.image_url) || undefined,
          },
  }
}

function recentToRow(recent: HistoryRecentLike): HistoryRow {
  // A local recent's `tab` is the translator tab it was produced on, which
  // uses the same 'text' | 'image' | 'pdf' vocabulary as content_type.
  const kind = historyRowKind(recent.tab)
  const inputText = text(recent.inputText)
  return {
    id: text(recent.id),
    kind,
    inputText: inputText || (kind === 'text' ? '' : mediaRowLabel(kind)),
    output: text(recent.output),
    sourceLang: text(recent.sourceLang) || 'en',
    targetLang: text(recent.targetLang) || 'he',
    ts: typeof recent.ts === 'number' && Number.isFinite(recent.ts) ? recent.ts : 0,
    // A local recent has no stored file or image URL to reopen, so it stays
    // restorable-as-text rather than pointing the modal at nothing.
    media: undefined,
  }
}

function byNewest(a: HistoryRow, b: HistoryRow): number {
  return b.ts - a.ts
}

/** Ledger entries -> rows. Newest first, capped, blank-id rows dropped (an
 * id is what the restore/open click and the React key both need). */
export function rowsFromLedger(
  entries: readonly HistoryLedgerEntryLike[] | null | undefined,
  cap: number = HISTORY_PANEL_CAP,
  previewOf: PreviewOf = identityPreview,
): HistoryRow[] {
  if (!Array.isArray(entries)) return []
  return entries
    .map((entry) => ledgerEntryToRow(entry, previewOf))
    .filter((row) => row.id !== '')
    .sort(byNewest)
    .slice(0, cap)
}

/** Local recents -> rows. Same ordering and cap. */
export function rowsFromRecents(
  recents: readonly HistoryRecentLike[] | null | undefined,
  cap: number = HISTORY_PANEL_CAP,
): HistoryRow[] {
  if (!Array.isArray(recents)) return []
  return recents
    .map(recentToRow)
    .filter((row) => row.id !== '')
    .sort(byNewest)
    .slice(0, cap)
}

export type HistoryFilter = 'all' | 'text' | 'files'

/** Filter + free-text search, applied in that order. Presentation only — it
 * never refetches, so typing stays instant across the whole capped list. */
export function filterHistoryRows(rows: readonly HistoryRow[], filter: HistoryFilter, query: string): HistoryRow[] {
  const q = query.trim().toLowerCase()
  return rows.filter((row) => {
    if (filter === 'text' && row.kind !== 'text') return false
    if (filter === 'files' && row.kind === 'text') return false
    if (!q) return true
    return row.inputText.toLowerCase().includes(q) || row.output.toLowerCase().includes(q)
  })
}

/**
 * Which source the panel should render.
 *
 * The account ledger wins whenever it actually returned rows. An empty
 * account list falls back to the local recents rather than showing "nothing
 * here" to someone whose current browser plainly has translations in it — the
 * common case being a first session before anything has synced up.
 */
export function pickHistorySource(params: {
  signedIn: boolean
  ledgerRows: readonly HistoryRow[] | null | undefined
  localRows: readonly HistoryRow[]
}): { rows: HistoryRow[]; source: 'ledger' | 'local' } {
  const { signedIn, ledgerRows, localRows } = params
  if (signedIn && Array.isArray(ledgerRows) && ledgerRows.length > 0) {
    return { rows: [...ledgerRows], source: 'ledger' }
  }
  return { rows: [...localRows], source: 'local' }
}
